import {
  Check,
  Circle,
  CircleCheck,
  Currency,
  HandCoins,
  Sigma,
  X,
} from "lucide-react";
import { useState } from "react";
import LoanCard from "../../shared/LoanCard";
import { SummaryCard } from "../../shared/LoanCard";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import LoanPill from "../../shared/LoanPill";
import { BarChart, Bar, XAxis, YAxis } from "recharts";
import NotAuthorized from "../../components/not-authorized/NotAuthorized";
import { SkeletonDashboard } from "../../skeletons/DashboardSkeleton";
import MyLoanCard from "../../shared/MyLoanCard";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useQuery } from "react-query";
import { getLoanApplicationStatistics } from "../../sdk/applications/applications";
import {
  getLoanDistributionByProduct,
  getLoanOfficerPerformance,
  getLoanStatistics,
  getLoanSummaryReport,
  getLoanTotals,
  getLoanTotalsByStatus,
} from "../../sdk/loans/loans";
import { getLoanProducts } from "../../sdk/products/products";
import { useIncrementMeta } from "../../hooks/useIncrementMeta";
import { formatNumber } from "../../utils/number";
import StackedBarChart from "../../components/charts/StackedBarChart";
import { getLoanProductSummary } from "../../sdk/reports/reports";

const Homepage = () => {
  const [openUnathorized, setOpenAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [activeLoanMetricTab, setActiveLoanMetricTab] = useState("All");
  const [activeApplicationTab, setActiveApplicationTab] = useState("All");
  const formatAmount = useFormatAmount();
  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };
  const today = new Date();
  const [fromDate, setFromDate] = useState("");
  const [loanMetricFromDate, setloanMetricFromDate] = useState("");
  const [applicationFromDate, setApplicationFromDate] = useState("");
  const [toDate, setToDate] = useState(formatDate(today));
  const [loanMetricToDate, setLoanMetricToDate] = useState(formatDate(today));
  const [applicationToDate, setApplicationToDate] = useState(formatDate(today));
  const [loanProductsStatistics, setLoanProductsStatistics] = useState([]);

  const { data: statistics, isLoading } = useQuery({
    queryKey: ["statistics", applicationFromDate, applicationToDate],
    queryFn: async () => {
      const response = await getLoanApplicationStatistics(
        applicationFromDate,
        applicationToDate,
      );
      return response.data.data;
    },
  });

  const { data: loanStatistics } = useQuery({
    queryKey: ["loan-statistics", fromDate, toDate],
    queryFn: async () => {
      const response = await getLoanStatistics(fromDate, toDate);
      return response.data.data;
    },
  });

  const { data: loanSummary } = useQuery({
    queryKey: ["loan-summary", loanMetricFromDate, loanMetricToDate],
    queryFn: async () => {
      const response = await getLoanSummaryReport(
        loanMetricFromDate,
        loanMetricToDate,
      );
      return response.data.data?.data;
    },
  });

  const { data: loanProducts } = useQuery({
    queryKey: ["loan-products"],
    queryFn: async () => {
      const response = await getLoanProducts();
      return response.data;
    },
    initialData: [],
  });

  const { data: loanTotalStatistics } = useQuery({
    queryKey: ["loan-totals"],
    queryFn: () => getLoanTotals(),
    initialData: [],
  });

  const { data: loanOfficerPerfomance } = useQuery({
    queryKey: ["loan-officer-performance"],
    queryFn: () => getLoanOfficerPerformance(),
    initialData: [],
  });

  const { data: loanTotalByStatus } = useQuery({
    queryKey: ["loan-total-by-status"],
    queryFn: () => getLoanTotalsByStatus(),
    initialData: [],
  });

  const { data: productsDistribution } = useQuery({
    queryKey: ["loan-products-distribution"],
    queryFn: () => getLoanDistributionByProduct(),
    initialData: [],
  });

  useQuery({
    queryKey: ["get loan product summary"],
    queryFn: async () => {
      const response = await getLoanProductSummary();
      return response?.data?.data;
    },
    onSuccess: (data) => {
      setLoanProductsStatistics(data);
    },
  });

  const tabs = [
    {
      label: "All",
      date: "All",
    },
    {
      label: "Today",
      date: new Date().toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    },
    {
      label: "Past 7 days",
      date: (() => {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 7);
        return `${past.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${today.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      })(),
    },
    {
      label: "This month",
      date: new Date().toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      }),
    },
  ];
  const applicationTabs = [
    {
      label: "All",
      date: "All",
    },
    {
      label: "Today",
      date: new Date().toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    },
    {
      label: "Past 7 days",
      date: (() => {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 7);
        return `${past.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${today.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      })(),
    },
    {
      label: "This month",
      date: new Date().toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      }),
    },
  ];
  const loanMetricsTabs = [
    {
      label: "All",
      date: "All",
    },
    {
      label: "Today",
      date: new Date().toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    },
    {
      label: "Past 7 days",
      date: (() => {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 7);
        return `${past.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })} - ${today.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
      })(),
    },
    {
      label: "This month",
      date: new Date().toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      }),
    },
  ];

  const getDateRange = (tab) => {
    const today = new Date();
    if (tab === "All") {
      return {
        from: "",
        to: "",
      };
    }

    if (tab === "Today") {
      return {
        from: today.toISOString().split("T")[0],
        to: today.toISOString().split("T")[0],
      };
    }

    if (tab === "Past 7 days") {
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      return {
        from: past.toISOString().split("T")[0],
        to: today.toISOString().split("T")[0],
      };
    }

    if (tab === "This month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        from: firstDay.toISOString().split("T")[0],
        to: lastDay.toISOString().split("T")[0],
      };
    }

    return { from: null, to: null };
  };

  const handleTabClick = (label) => {
    setActiveTab(label);
    const { from, to } = getDateRange(label);
    setFromDate(from);
    setToDate(to);
  };

  const handleLoanMetricTabClick = (label) => {
    setActiveLoanMetricTab(label);
    const { from, to } = getDateRange(label);
    setloanMetricFromDate(from);
    setLoanMetricToDate(to);
  };

  const handleLoanApplicationTabClick = (label) => {
    setActiveApplicationTab(label);
    const { from, to } = getDateRange(label);
    setApplicationFromDate(from);
    setApplicationToDate(to);
  };

  const selectedTab = tabs.find((tab) => tab.label === activeTab);
  const selectedLoanMetricTab = loanMetricsTabs.find(
    (tab) => tab.label === activeLoanMetricTab,
  );
  const selectedLoanApplicationTab = applicationTabs.find(
    (tab) => tab.label === activeApplicationTab,
  );
  const COLORS = [
    "#000000",
    "#FF8042",
    "#00A676",
    "#000000",
    "#A3008B",
    "#00A395",
    "#A30003",
    "#FFC300",
    "#008B8B",
    "#6A0DAD",
    "#228B22",
  ];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    if (percent === 0) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        fontSize="13px"
        fontFamily="Arial"
        fontWeight="bold"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const closeNotAuthorized = () => {
    setOpenAuthorized(false);
  };

  const total = loanStatistics?.total ?? "0";
  const { increment: loanIncrement, type: loanIncrementType } =
    useIncrementMeta(total, "prevTotalLoans");

  return (
    <>
      {openUnathorized && (
        <NotAuthorized closeNotAuthorized={closeNotAuthorized} />
      )}
      {isLoading ? (
        <SkeletonDashboard />
      ) : (
        <div className="pl-[10px]">
          <div className="h-[60px] flex items-center justify-between">
            <p className="font-bold text-[28px] text-[#222222]">Dashboard</p>
          </div>
          <div className="h-[300px] w-full rounded-[5px] px-[10px] mb-[15px] py-[20px] relative bg-white">
            <p className="text-[#222222] absolute top-[20px] left-[10px] text-[15px] font-bold">
              Loans
            </p>
            <div className="flex items-center justify-center mb-[20px] gap-[10px]">
              {tabs.map((tab) => (
                <div
                  key={tab.label}
                  onClick={() => handleTabClick(tab.label)}
                  className={`w-[110px] cursor-pointer h-[25px] flex items-center justify-center rounded-[30px] text-[14px] ${
                    activeTab === tab.label
                      ? "bg-[#000000] text-white"
                      : "bg-[#F3F3F3] text-[#222222]"
                  }`}
                >
                  {tab.label}
                </div>
              ))}
            </div>
            <div className="w-full flex items-center justify-center">
              <div className="w-[266px] h-[25px] flex items-center justify-center rounded-[30px] bg-[#F3F3F3] text-[#000000] text-[14px]">
                {selectedTab?.date}
              </div>
            </div>
            <div className="flex items-center my-[20px] justify-between">
              <LoanCard
                icon={<Sigma size={16} />}
                label="Total loans"
                value={loanStatistics?.total ?? 0}
                increment={loanIncrement}
                type={loanIncrementType}
                padColor="#4B5D67"
              />
              <LoanCard
                icon={<Circle color="#00A676" size={16} />}
                label="Active loans"
                value={loanStatistics?.active ?? 0}
                increment="+0.00%"
                padColor="#00A676"
              />
              <LoanCard
                icon={<CircleCheck color="#F4A261" size={16} />}
                label="Pending loans"
                value={loanStatistics?.["pending"] ?? 0}
                increment="+0.00%"
                padColor="#F4A261"
              />
              <LoanCard
                icon={<X color="#000000" size={16} />}
                label="Defaulted loans"
                value={loanStatistics?.defaulted ?? 0}
                increment="+0.00%"
                type="negative"
                padColor="#E63946"
              />
              <LoanCard
                icon={<Check color="#ADB5BD" size={16} />}
                label="Cleared"
                value={loanStatistics?.["cleared"] ?? 0}
                increment="0.00%"
                type="positive"
                padColor="#ADB5BD"
              />
            </div>
          </div>
          <div className="w-full rounded-[5px] px-[10px] mb-[15px] py-[20px] relative bg-white">
            <p className="text-[#222222] text-[15px] font-bold mb-[10px]">
              Loan Metrics
            </p>
            <div className="flex items-center justify-center mb-[20px] gap-[10px]">
              {loanMetricsTabs.map((tab) => (
                <div
                  key={tab.label}
                  onClick={() => handleLoanMetricTabClick(tab.label)}
                  className={`w-[110px] cursor-pointer h-[25px] flex items-center justify-center rounded-[30px] text-[14px] ${
                    activeLoanMetricTab === tab.label
                      ? "bg-[#000000] text-white"
                      : "bg-[#F3F3F3] text-[#222222]"
                  }`}
                >
                  {tab.label}
                </div>
              ))}
            </div>
            <div className="w-full flex items-center justify-center">
              <div className="w-[266px] h-[25px] flex items-center justify-center rounded-[30px] bg-[#F3F3F3] text-[#000000] text-[14px]">
                {selectedLoanMetricTab?.date}
              </div>
            </div>
            <div className="flex items-center my-[20px] justify-between">
              <SummaryCard
                icon={<Sigma size={16} />}
                label="Loan Principal Total"
                value={formatAmount(loanSummary?.loanPrincipalTotal ?? 0)}
                increment={loanIncrement}
                type={loanIncrementType}
                padColor="#000000"
              />
              <SummaryCard
                icon={<Circle color="#00A676" size={16} />}
                label="Total Interest Amount"
                value={formatAmount(loanSummary?.totalInterestAmount ?? 0)}
                increment="+0.00%"
                padColor="#00A676"
              />
              <SummaryCard
                icon={<CircleCheck color="#F4A261" size={16} />}
                label="Penalties"
                value={formatAmount(loanSummary?.penalties ?? 0)}
                increment="+0.00%"
                padColor="#F4A261"
              />
              <SummaryCard
                icon={<X color="#000000" size={16} />}
                label="Total Arrears"
                value={formatAmount(loanSummary?.totalArrears ?? 0)}
                increment="+0.00%"
                type="negative"
                padColor="#E63946"
              />
              <SummaryCard
                icon={<Check color="#ADB5BD" size={16} />}
                label="Total Repayments"
                value={formatAmount(loanSummary?.["totalRepayments"] ?? 0)}
                increment="0.00%"
                type="positive"
                padColor="#ADB5BD"
              />
            </div>
          </div>
          <div className="w-full rounded-[10px] bg-white my-[20px] p-[10px]">
            <p className="text-[#222222] mb-[10px] text-[16px] font-bold">
              Loan Applications
            </p>
            <div className="flex items-center justify-center mb-[20px] gap-[10px]">
              {applicationTabs.map((tab) => (
                <div
                  key={tab.label}
                  onClick={() => handleLoanApplicationTabClick(tab.label)}
                  className={`w-[110px] cursor-pointer h-[25px] flex items-center justify-center rounded-[30px] text-[14px] ${
                    activeApplicationTab === tab.label
                      ? "bg-[#000000] text-white"
                      : "bg-[#F3F3F3] text-[#222222]"
                  }`}
                >
                  {tab.label}
                </div>
              ))}
            </div>
            <div className="w-full mb-[20px] flex items-center justify-center">
              <div className="w-[266px] h-[25px] flex items-center justify-center rounded-[30px] bg-[#F3F3F3] text-[#000000] text-[14px]">
                {selectedLoanApplicationTab?.date}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="w-[16%]">
                <MyLoanCard
                  icon={<Circle color="#000000" size={16} />}
                  label="Total applications"
                  value={statistics?.total ?? 0}
                  padColor="#4B5D67"
                  backgroundColor="#FFFFFF"
                />
              </div>
              <div className="w-[16%]">
                <MyLoanCard
                  icon={<Circle color="#000000" size={16} />}
                  label="Incomplete applications"
                  value={statistics?.incomplete ?? 0}
                  padColor="#000000"
                  backgroundColor="#FFDEDE"
                />
              </div>
              <div className="w-[16%]">
                <MyLoanCard
                  icon={<Circle color="#F4A261" size={16} />}
                  label="Pending approval"
                  value={statistics?.["pendingApproval"] ?? 0}
                  border="#D6D6D6"
                  padColor="#F4A261"
                  backgroundColor="#FFF4EB"
                />
              </div>
              <div className="w-[16%]">
                <MyLoanCard
                  icon={<Circle color="#00A676" size={16} />}
                  label="Approved applications"
                  value={statistics?.approved ?? 0}
                  padColor="#00A676"
                  backgroundColor="#FFFFFF"
                />
              </div>
              <div className="w-[16%]">
                <MyLoanCard
                  icon={<Circle color="#000000" size={16} />}
                  label="Rejected applications"
                  value={statistics?.declined ?? 0}
                  padColor="#000000"
                  backgroundColor="#FFFFFF"
                />
              </div>
              <div className="w-[16%]">
                <MyLoanCard
                  icon={<Circle color="#000000" size={16} />}
                  label="Disbursed applications"
                  value={statistics?.disbursed ?? 0}
                  padColor="#000000"
                  backgroundColor="#FFFFFF"
                />
              </div>
            </div>
          </div>
          <div className="h-[450px] flex justify-between w-full rounded-[10px] px-[10px] mb-[15px] py-[20px] bg-white">
            <div className="w-[30%] h-full flex flex-col justify-between">
              <p className="text-[#222222] text-[15px] font-bold mb-[10px]">
                Loan Portfolio
              </p>
              <div className="w-[100%] h-[149px] border mb-[10px] border-[#E6E6E6] p-[20px] rounded-[10px]">
                <div className="flex items-center gap-[10px]">
                  <div className="h-[32px] w-[32px] rounded-full bg-[#F3F3F3] flex items-center justify-center">
                    <HandCoins size={16} />
                  </div>
                  <p className="text-[#222222] text-[14px] font-normal">
                    Total loan volume
                  </p>
                </div>
                <p className="text-[#000000] mt-[15px] text-[28px] font-bold text-center">
                  {formatAmount(loanTotalStatistics?.totalLoanBook ?? 0)}
                </p>
              </div>
              <div className="w-[100%] flex items-center justify-between">
                <div className="h-[149px] flex flex-col justify-around w-[162px] border border-[#E6E6E6] p-[10px] rounded-[10px]">
                  <div className="w-full flex items-center justify-center">
                    <div className="h-[32px] w-[32px] rounded-full bg-[#F3F3F3] flex items-center justify-center">
                      <HandCoins size={16} />
                    </div>
                  </div>
                  <p className="text-[#222222] text-[12px] px-[20px] text-center font-normal">
                    Total Amount Loan in Arrears
                  </p>
                  <p className="text-[#222222] font-bold text-[24px] text-center">
                    {formatNumber(loanTotalStatistics?.totalArrears ?? 0)}
                  </p>
                </div>
                <div className="h-[149px] flex flex-col justify-around w-[162px] border border-[#E6E6E6] p-[10px] rounded-[10px]">
                  <div className="w-full flex items-center justify-center">
                    <div className="h-[32px] w-[32px] rounded-full bg-[#F3F3F3] flex items-center justify-center">
                      <Currency size={16} />
                    </div>
                  </div>
                  <p className="text-[#222222] text-[12px] px-[20px] text-center font-normal">
                    Total amount of loans defaulted
                  </p>
                  <p className="text-[#222222] font-bold text-[24px] text-center">
                    {formatNumber(
                      loanTotalStatistics?.totalDefaultedAmount ?? 0,
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="w-[38%] h-full">
              <p className="text-[#222222] font-bold mb-[5px] text-[15px]">
                Loan Portfolio Mix by Product Type
              </p>
              {productsDistribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height="75%">
                  <PieChart width={500} height={500}>
                    <Pie
                      data={productsDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                      stroke="none"
                    >
                      {productsDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <circle cx="50%" cy="50%" r={30} fill="white" />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-gray-500 text-sm">
                  Loan products distribution data unavailable
                </div>
              )}
              <div className="w-full h-[18%] mt-[4px] flex flex-wrap items-center justify-center gap-[5px]">
                {productsDistribution.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-center items-center gap-[5px]"
                  >
                    <div
                      style={{ backgroundColor: COLORS[index] }}
                      className={`h-[14px] w-[14px] rounded-full`}
                    ></div>
                    <p className="text-[#000000] capitalize text-[12px] truncate font-medium">
                      {item.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-[30%] h-full">
              <p className="text-[#222222] font-bold mb-[5px] text-[15px]">
                Loans Products
              </p>
              <div className="flex h-[95%] p-[10px] flex-col gap-[20px] overflow-y-auto">
                {loanProducts?.length > 0 ? (
                  loanProducts?.map((product) => (
                    <LoanPill key={product?.id} loanType={product?.name} />
                  ))
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <p>Loan products not available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="h-[500px] flex justify-between mb-[20px]">
            <div className="w-[49%] p-[10px] rounded-[10px] h-full bg-white">
              <p className="text-[#222222] h-[10%] text-[15px] font-bold">
                Products Breakdown
              </p>
              {loanProductsStatistics?.length > 0 ? (
                <div className="h-[90%] w-full">
                  <StackedBarChart
                    loanProductsStatistics={loanProductsStatistics}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-gray-500 text-sm">
                  Loan products distribution data unavailable
                </div>
              )}
            </div>
            <div className="w-[49%] p-[10px] rounded-[10px] h-full bg-white">
              <p className="text-[#222222] text-[15px] mb-[10px] font-bold">
                Loans Distribution by Status
              </p>
              {loanTotalByStatus?.length > 0 ? (
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart
                    width={500}
                    height={300}
                    data={loanTotalByStatus?.filter((item) => item.value !== 0)}
                    margin={{
                      top: 15,
                      right: 15,
                      left: 0,
                      bottom: 20,
                    }}
                  >
                    <Tooltip cursor={false} />
                    <XAxis
                      dataKey="name"
                      type="category"
                      tick={{ fontSize: 12 }}
                      label={{
                        value: "Status",
                        position: "bottom",
                        offset: 0,
                        style: { textAnchor: "middle", fontSize: 14 },
                      }}
                    />
                    <YAxis
                      type="number"
                      label={{
                        value: "Number of loans",
                        angle: -90,
                        position: "insideLeft",
                        offset: 20,
                        style: { textAnchor: "middle", fontSize: 14 },
                      }}
                    />
                    <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                      {loanTotalByStatus.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-gray-500 text-sm">
                  Loan totals by status unavailable
                </div>
              )}

              <div className="w-[85%] mt-[5px] mx-auto flex items-center justify-center gap-[5px]">
                {loanTotalByStatus.map((item, index) => (
                  <div
                    key={index}
                    className="flex w-full justify-center items-center gap-[5px]"
                  >
                    <div
                      style={{ backgroundColor: COLORS[index] }}
                      className={`h-[14px] w-[14px] rounded-full`}
                    ></div>
                    <p className="text-[#000000] text-[12px] truncate font-medium">
                      {item.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="h-[452px] p-[10px] rounded-[10px] bg-white w-full mb-[20px]">
            <p className="text-[#222222] text-[15px] font-bold mb-[10px]">
              Loan Officers - Performance
            </p>
            {loanOfficerPerfomance?.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart
                  width={500}
                  height={300}
                  data={loanOfficerPerfomance}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 10,
                    bottom: 15,
                  }}
                >
                  <Tooltip cursor={false} />
                  <XAxis
                    dataKey="name"
                    label={{
                      value: "Loan officers",
                      angle: 0,
                      position: "insideBottom",
                      offset: -10,
                      style: { textAnchor: "middle", fontSize: "14" },
                    }}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    label={{
                      value: "Number of loans",
                      angle: -90,
                      position: "insideLeft",
                      offset: 15,
                      style: { textAnchor: "middle" },
                    }}
                  />
                  <Bar radius={[10, 10, 0, 0]} dataKey="numberOfLoans">
                    {loanOfficerPerfomance.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-gray-500 text-sm">
                Loan officer performance data unavailable
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Homepage;
